import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { User } from "../../model_gen/company";
import { CompanyService } from "../../services/CompanyService";
import { UserPlusIcon, AlertCircleIcon, Loader2Icon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import UsersTable from "./UsersTable";
import PendingUsersTable from "./PendingUsersTable";

const Users: React.FC = () => {
  const { apiKey } = usePermissions();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [apiKey]);

  const fetchUsers = () => {
    if (isLoading) return;

    setIsLoading(true);
    CompanyService.getOrganizationUsers(setIsLoading, apiKey).subscribe(
      (data: User[]) => {
        setUsers(data);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching users:", error);
        setIsLoading(false);
      }
    );
  };

  // Separate active and pending users
  const activeUsers = users.filter((user) => !user.pending);
  const pendingUsers = users.filter((user) => user.pending);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOpenInviteModal = () => {
    setInviteEmail("");
    setInviteError("");
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    if (!isInviting) {
      setIsInviteModalOpen(false);
      setInviteEmail("");
      setInviteError("");
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInviteUser = () => {
    setInviteError("");

    if (!inviteEmail.trim()) {
      setInviteError("Email is required");
      return;
    }

    if (!validateEmail(inviteEmail)) {
      setInviteError("Please enter a valid email address");
      return;
    }

    setIsInviting(true);
    CompanyService.inviteUserToOrganization(
      inviteEmail,
      "",
      null,
      apiKey
    ).subscribe(
      () => {
        setIsInviting(false);
        setIsInviteModalOpen(false);
        setInviteEmail("");
        toast.success(`Invitation sent to ${inviteEmail}`);
        fetchUsers();
      },
      (error: any) => {
        setIsInviting(false);
        const errorMessage =
          error?.error?.message || error?.message || "Failed to invite user";

        if (
          errorMessage.includes("not set up for user management") ||
          errorMessage.includes("contact support")
        ) {
          setInviteError(errorMessage);
        } else {
          setInviteError(errorMessage);
          toast.error(errorMessage);
        }
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Users</h3>
          <p className="text-sm text-muted-foreground">
            Manage organization members and invitations
          </p>
        </div>
        <Button onClick={handleOpenInviteModal} className="gap-2">
          <UserPlusIcon className="w-4 h-4" />
          Invite User
        </Button>
      </div>

      {/* Active Users */}
      <UsersTable
        users={activeUsers}
        loading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
      />

      {/* Pending Users */}
      <PendingUsersTable
        pendingUsers={pendingUsers}
        loading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
      />

      {/* Invite User Modal */}
      <Dialog
        open={isInviteModalOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseInviteModal();
        }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation to join your organization. They'll receive an
              email with instructions to sign up.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {inviteError && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertCircleIcon className="w-4 h-4 text-destructive flex-shrink-0" />
                <p className="text-sm text-destructive">{inviteError}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="invite-email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isInviting) {
                    handleInviteUser();
                  }
                }}
                disabled={isInviting}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseInviteModal}
              disabled={isInviting}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleInviteUser}
              disabled={isInviting || !inviteEmail.trim()}>
              {isInviting && (
                <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
              )}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
